/**
 * Post-session "Who was this?" prompt.
 *
 * Phase 3f. After a session finishes processing, any cluster Meridian
 * couldn't auto-identify becomes a provisional speaker. This modal
 * lists them one per card; the user picks a contact (from their
 * synced Apple Contacts), taps "Someone new" to create a stub, taps
 * "Not a person" for music / TV / overlap, or "Skip" to defer.
 *
 * Design principle: invisible infrastructure. No "voiceprint" word in
 * the copy anywhere. Never tell the user how many samples we have or
 * what the cosine was. Just "who was this?" and then it's handled.
 */
import { useMemo, useState } from "react";
import { invoke } from "@tauri-apps/api/core";
import { AlertTriangle, Play, Search, Sparkles, UserPlus, X } from "lucide-react";

import type {
  EnrollmentQualityCheck,
  ProvisionalSpeaker,
  ProvisionalSuggestion,
} from "@/types/voice";
import type { Contact } from "@/lib/data";
import {
  checkEnrollmentQuality,
  resolveProvisionalSpeaker,
  resolveProvisionalAsSelf,
  rejectProvisionalSpeaker,
} from "@/hooks/useSessionProcessor";
import { displayNameForContactId } from "@/utils/interactionDisplay/interactionDisplay";

interface WhoWasThisPromptProps {
  provisionals: ProvisionalSpeaker[];
  contacts: Contact[];
  onCreateContact: (firstName: string) => Promise<Contact>;
  onDismiss: () => void;
  onAnyResolved: () => void;
}

type SlotStatus = "open" | "picking" | "creating" | "resolving" | "done";

interface SlotState {
  provisional: ProvisionalSpeaker;
  status: SlotStatus;
  error: string | null;
}

/**
 * Phase 3r — pending confirmation when the enrollment-quality gate
 * flags a candidate cluster. Holds the arguments for the original
 * resolve action so the dialog can re-invoke it after the user
 * explicitly confirms. `kind` distinguishes:
 *   - "similarVoice": the cluster centroid resembles an existing
 *     contact (similarContactName is populated)
 *   - "multipleVoices": the cluster's sub-windows disagree, likely
 *     contains more than one speaker
 */
type PendingConfirmKind = "similarVoice" | "multipleVoices";

interface PendingConfirm {
  provisionalId: string;
  kind: PendingConfirmKind;
  similarContactName: string | null;
  proceed: () => Promise<void>;
}

/**
 * Top-level modal. Shows one card per unresolved provisional speaker.
 * Dismisses itself when all are handled or the user cancels.
 */
export function WhoWasThisPrompt({
  provisionals,
  contacts,
  onCreateContact,
  onDismiss,
  onAnyResolved,
}: WhoWasThisPromptProps) {
  const [slots, setSlots] = useState<SlotState[]>(() =>
    provisionals.map((p) => ({ provisional: p, status: "open", error: null }))
  );
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(
    null
  );

  const remaining = slots.filter((s) => s.status !== "done").length;

  const updateSlot = (id: string, patch: Partial<SlotState>) => {
    setSlots((prev) =>
      prev.map((s) => (s.provisional.id === id ? { ...s, ...patch } : s))
    );
  };

  /**
   * Phase 3r — run the enrollment-quality gate, and if it warns,
   * stash the proceed callback for the confirmation dialog. Returns
   * true when the caller can go straight through, false when the
   * dialog has taken over.
   */
  const runQualityGate = async (
    provisionalId: string,
    intendedContactId: string | null,
    proceed: () => Promise<void>
  ): Promise<boolean> => {
    try {
      const check: EnrollmentQualityCheck = await checkEnrollmentQuality(
        provisionalId,
        intendedContactId
      );
      if (check.isClean) return true;

      if (check.warningCode === "multiple_voices") {
        setPendingConfirm({
          provisionalId,
          kind: "multipleVoices",
          similarContactName: null,
          proceed,
        });
        return false;
      }

      const similarName =
        check.similarVoiceprintId === "user_self"
          ? "You"
          : displayNameForContactId(
              check.similarContactId ?? check.similarVoiceprintId ?? "",
              contacts
            );
      setPendingConfirm({
        provisionalId,
        kind: "similarVoice",
        similarContactName: similarName,
        proceed,
      });
      return false;
    } catch {
      // Gate malfunction must never block a legitimate enrollment —
      // fall through as if the check said clean.
      return true;
    }
  };

  const commitResolveToContact = async (
    provisionalId: string,
    contactId: string
  ) => {
    updateSlot(provisionalId, { status: "resolving", error: null });
    try {
      await resolveProvisionalSpeaker(provisionalId, contactId);
      updateSlot(provisionalId, { status: "done" });
      onAnyResolved();
    } catch {
      updateSlot(provisionalId, {
        status: "open",
        error: "Could not save. Try again.",
      });
    }
  };

  const commitResolveAsSelf = async (provisionalId: string) => {
    updateSlot(provisionalId, { status: "resolving", error: null });
    try {
      await resolveProvisionalAsSelf(provisionalId);
      updateSlot(provisionalId, { status: "done" });
      onAnyResolved();
    } catch {
      updateSlot(provisionalId, {
        status: "open",
        error: "Could not save. Try again.",
      });
    }
  };

  const handlePickContact = async (provisionalId: string, contact: Contact) => {
    if (!contact.appleContactId) return;
    const contactId = contact.appleContactId;
    const proceed = () => commitResolveToContact(provisionalId, contactId);
    const ok = await runQualityGate(provisionalId, contactId, proceed);
    if (ok) await proceed();
  };

  // Phase 3n — the "sounds like" suggestion chips at the top of each
  // slot tap through here. `voiceprintId` is either a contact id or
  // the reserved "user_self"; we branch to the right resolver.
  //
  // Phase 3r — contact suggestion chips go straight through without
  // the quality gate (they're merging into an existing voiceprint,
  // not creating a new one). The "You" chip DOES run through the gate
  // because tapping it adapts user_self — same contamination risk as
  // the "This was me" button below.
  const handlePickSuggestion = async (
    provisionalId: string,
    suggestion: ProvisionalSuggestion
  ) => {
    if (suggestion.voiceprintId === "user_self") {
      const proceed = () => commitResolveAsSelf(provisionalId);
      const ok = await runQualityGate(provisionalId, "user_self", proceed);
      if (ok) await proceed();
      return;
    }
    const contactId = suggestion.contactId ?? suggestion.voiceprintId;
    await commitResolveToContact(provisionalId, contactId);
  };

  // Phase 3r — "This was me" runs through the same similarity gate
  // that protects contact enrollments. Passing "user_self" as the
  // excluded id makes the gate scan EVERY contact voiceprint for a
  // too-close match; if the cluster actually sounds more like Dad
  // than like the user, we warn before folding it into user_self.
  // This is the exact safety we didn't have when a prior "This was
  // me" on a merged TV cluster contaminated the anchor.
  const handleThisWasMe = async (provisionalId: string) => {
    const proceed = () => commitResolveAsSelf(provisionalId);
    const ok = await runQualityGate(provisionalId, "user_self", proceed);
    if (ok) await proceed();
  };

  const handleCreateNew = async (provisionalId: string, firstName: string) => {
    if (!firstName.trim()) return;

    // Phase 3r — check the enrollment quality BEFORE we create the
    // Apple contact. If the cluster sounds suspiciously like an
    // existing voiceprint, we don't want to leave a dangling empty
    // contact on confirm-cancel.
    const gateProceed = async () => {
      updateSlot(provisionalId, { status: "creating", error: null });
      try {
        const created = await onCreateContact(firstName.trim());
        if (!created.appleContactId) {
          throw new Error("Contact created without an identifier");
        }
        await resolveProvisionalSpeaker(provisionalId, created.appleContactId);
        updateSlot(provisionalId, { status: "done" });
        onAnyResolved();
      } catch {
        updateSlot(provisionalId, {
          status: "open",
          error: "Could not create contact. Try again.",
        });
      }
    };

    const ok = await runQualityGate(provisionalId, null, gateProceed);
    if (ok) await gateProceed();
  };

  const handleNotAPerson = async (provisionalId: string) => {
    updateSlot(provisionalId, { status: "resolving", error: null });
    try {
      await rejectProvisionalSpeaker(provisionalId);
      updateSlot(provisionalId, { status: "done" });
      onAnyResolved();
    } catch {
      updateSlot(provisionalId, {
        status: "open",
        error: "Could not save. Try again.",
      });
    }
  };

  const handleSkip = (provisionalId: string) => {
    updateSlot(provisionalId, { status: "done" });
  };

  // Auto-dismiss when all slots are handled.
  if (remaining === 0) {
    setTimeout(onDismiss, 50);
  }

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/70 [backdrop-filter:blur(10px)]"
        onClick={onDismiss}
      />
      <div
        className="relative z-10 flex max-h-[85vh] w-full flex-col overflow-hidden rounded-t-3xl sm:rounded-3xl sm:max-w-[420px] border border-[rgba(201,168,76,0.15)] bg-[rgba(18,16,14,0.98)] shadow-[0_-16px_80px_rgba(0,0,0,0.7),0_0_40px_rgba(201,168,76,0.04)] [backdrop-filter:blur(40px)_saturate(180%)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* Header */}
        <div className="relative flex items-center justify-between border-b border-[rgba(255,255,255,0.05)] px-5 py-4">
          <div>
            <h2 className="text-[17px] font-medium tracking-tight text-white">
              Who was there?
            </h2>
            <p className="mt-0.5 text-[12px] text-[rgba(255,255,255,0.4)]">
              Meridian heard {provisionals.length} voice
              {provisionals.length === 1 ? "" : "s"} it didn't recognize.
            </p>
          </div>
          <button
            onClick={onDismiss}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(255,255,255,0.04)] active:bg-[rgba(255,255,255,0.08)]"
            aria-label="Dismiss"
          >
            <X size={16} className="text-[rgba(255,255,255,0.5)]" />
          </button>
        </div>

        {/* Slots */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="flex flex-col gap-4">
            {slots.map((slot) =>
              slot.status === "done" ? null : (
                <SpeakerSlot
                  key={slot.provisional.id}
                  slot={slot}
                  contacts={contacts}
                  onPickContact={handlePickContact}
                  onPickSuggestion={handlePickSuggestion}
                  onThisWasMe={handleThisWasMe}
                  onCreateNew={handleCreateNew}
                  onNotAPerson={handleNotAPerson}
                  onSkip={handleSkip}
                />
              )
            )}
          </div>
        </div>
      </div>

      {pendingConfirm && (
        <EnrollmentGateConfirm
          kind={pendingConfirm.kind}
          similarName={pendingConfirm.similarContactName}
          onConfirm={async () => {
            const pending = pendingConfirm;
            setPendingConfirm(null);
            await pending.proceed();
          }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}

// ── Phase 3r — enrollment gate confirmation overlay ──

interface EnrollmentGateConfirmProps {
  kind: PendingConfirmKind;
  similarName: string | null;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Stacks above the WhoWasThisPrompt modal when the enrollment-quality
 * gate flags a candidate cluster. Two distinct cases share the same
 * visual shell but different copy — "sounds like X" (Phase 3r-1) and
 * "we heard multiple voices" (Phase 3r-4). Both keep the cancel path
 * as the softer default and never leak mechanism vocabulary like
 * "voiceprint" or "cosine" into the dialog.
 */
function EnrollmentGateConfirm({
  kind,
  similarName,
  onConfirm,
  onCancel,
}: EnrollmentGateConfirmProps) {
  const title =
    kind === "multipleVoices"
      ? "We heard more than one voice"
      : `This sounds like ${similarName ?? "someone you know"}`;
  const body =
    kind === "multipleVoices"
      ? "Meridian picked up what sounds like more than one person in this sample. If it really is multiple people, go back and pick the one you meant. Creating a new person from a mixed sample will make future recognition less accurate."
      : `Meridian thinks this voice might be ${similarName}. If it's the same person, go back and pick ${similarName} instead. If it's really someone different, continue.`;

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center px-6"
      role="alertdialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/70 [backdrop-filter:blur(14px)]"
        onClick={onCancel}
      />
      <div
        className="relative z-10 flex w-full max-w-[360px] flex-col gap-4 rounded-3xl border border-[rgba(201,168,76,0.25)] bg-[rgba(24,21,18,0.98)] p-6 shadow-[0_20px_80px_rgba(0,0,0,0.7),0_0_60px_rgba(201,168,76,0.08)]"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle
            size={16}
            className="text-[rgba(201,168,76,0.85)]"
          />
          <h3 className="text-[15px] font-medium tracking-tight text-white">
            {title}
          </h3>
        </div>
        <p className="text-[13px] leading-relaxed text-[rgba(255,255,255,0.65)]">
          {body}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl bg-[rgba(255,255,255,0.06)] py-2.5 text-[14px] font-medium text-white active:bg-[rgba(255,255,255,0.1)]"
          >
            Go back
          </button>
          <button
            type="button"
            onClick={() => {
              void onConfirm();
            }}
            className="flex-1 rounded-xl bg-[rgba(201,168,76,0.85)] py-2.5 text-[14px] font-medium text-black active:bg-[rgba(201,168,76,1)]"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

// ── One slot — one provisional speaker ──

interface SpeakerSlotProps {
  slot: SlotState;
  contacts: Contact[];
  onPickContact: (provisionalId: string, contact: Contact) => void;
  onPickSuggestion: (
    provisionalId: string,
    suggestion: ProvisionalSuggestion
  ) => void;
  onThisWasMe: (provisionalId: string) => void;
  onCreateNew: (provisionalId: string, firstName: string) => void;
  onNotAPerson: (provisionalId: string) => void;
  onSkip: (provisionalId: string) => void;
}

function SpeakerSlot({
  slot,
  contacts,
  onPickContact,
  onPickSuggestion,
  onThisWasMe,
  onCreateNew,
  onNotAPerson,
  onSkip,
}: SpeakerSlotProps) {
  const { provisional, status, error } = slot;
  const [mode, setMode] = useState<"root" | "picking" | "creating">("root");
  const busy = status === "resolving" || status === "creating";

  const preview =
    provisional.transcriptPreview?.trim() ||
    "(no transcript available for this voice)";

  return (
    <div className="overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)]">
      {/* Preview row */}
      <div className="flex items-start gap-3 px-4 pb-3 pt-4">
        {provisional.sampleAudioPath ? (
          <PlayButton path={provisional.sampleAudioPath} />
        ) : (
          <div className="h-9 w-9 shrink-0" />
        )}
        <p className="flex-1 text-[14px] leading-relaxed text-[rgba(255,255,255,0.75)]">
          {preview.length > 140 ? `${preview.slice(0, 140)}…` : preview}
        </p>
      </div>

      {error && (
        <p className="border-t border-[rgba(255,70,70,0.2)] bg-[rgba(255,70,70,0.08)] px-4 py-2 text-[12px] text-[rgba(255,120,120,0.9)]">
          {error}
        </p>
      )}

      {/* Phase 3n — top-N suggestion chips. Color tiers encode
          confidence quietly; cosine numbers never appear in the
          product surface. Tapping a chip resolves immediately. */}
      {mode === "root" && !busy && provisional.suggestions.length > 0 && (
        <SuggestionStrip
          suggestions={provisional.suggestions}
          contacts={contacts}
          onPick={(suggestion) => onPickSuggestion(provisional.id, suggestion)}
        />
      )}

      {/* Mode-switched actions */}
      {mode === "root" && !busy && (
        <div className="flex flex-col border-t border-[rgba(255,255,255,0.05)]">
          <SlotAction
            label="Pick a contact"
            onClick={() => setMode("picking")}
          />
          <SlotAction
            label="Someone new"
            onClick={() => setMode("creating")}
          />
          <SlotAction
            label="This was me"
            onClick={() => onThisWasMe(provisional.id)}
          />
          <SlotAction
            label="Not a person"
            onClick={() => onNotAPerson(provisional.id)}
            subtle
          />
          <SlotAction
            label="Skip for now"
            onClick={() => onSkip(provisional.id)}
            subtle
          />
        </div>
      )}

      {mode === "picking" && !busy && (
        <ContactPicker
          contacts={contacts}
          onPick={(c) => onPickContact(provisional.id, c)}
          onCancel={() => setMode("root")}
        />
      )}

      {mode === "creating" && !busy && (
        <NewContactForm
          onSubmit={(name) => onCreateNew(provisional.id, name)}
          onCancel={() => setMode("root")}
        />
      )}

      {busy && (
        <div className="border-t border-[rgba(255,255,255,0.05)] px-4 py-4 text-center text-[13px] text-[rgba(255,255,255,0.5)]">
          {status === "creating" ? "Adding them…" : "Saving…"}
        </div>
      )}
    </div>
  );
}

// ── Play button — uses the existing plugin playback ──

function PlayButton({ path }: { path: string }) {
  const [playing, setPlaying] = useState(false);
  const handlePlay = async () => {
    if (playing) {
      await invoke("plugin:voice|stop_playback");
      setPlaying(false);
      return;
    }
    setPlaying(true);
    try {
      await invoke("plugin:voice|play_recording", { path });
    } catch {
      // Playback resolves when audio naturally ends — errors (missing
      // file, permission glitch) are rare. Fail silent; user can tap
      // again.
    } finally {
      setPlaying(false);
    }
  };

  return (
    <button
      onClick={handlePlay}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(201,168,76,0.15)] active:scale-95 active:bg-[rgba(201,168,76,0.25)]"
      aria-label={playing ? "Stop playback" : "Play voice sample"}
    >
      <Play
        size={13}
        fill="currentColor"
        className="translate-x-[1px] text-[rgba(201,168,76,0.9)]"
      />
    </button>
  );
}

// ── Phase 3n — "sounds like" suggestion strip ──

/**
 * Color tier for a suggestion chip. Cosine mapping is the only place
 * the raw score is read; after this it's a qualitative warmth level
 * that the design system understands. Tiers are deliberately coarse —
 * three steps make relative confidence legible without training the
 * user to parse numbers.
 *
 *   warmer: ≥ 0.7  — confident, same as auto-match threshold
 *   warm:   ≥ 0.55 — likely, worth leading with
 *   cool:   ≥ 0.35 — worth showing, but a longer shot
 */
type ConfidenceTier = "warmer" | "warm" | "cool";

function tierForCosine(cosine: number): ConfidenceTier {
  if (cosine >= 0.7) return "warmer";
  if (cosine >= 0.55) return "warm";
  return "cool";
}

interface SuggestionStripProps {
  suggestions: ProvisionalSuggestion[];
  contacts: Contact[];
  onPick: (suggestion: ProvisionalSuggestion) => void;
}

function SuggestionStrip({
  suggestions,
  contacts,
  onPick,
}: SuggestionStripProps) {
  return (
    <div className="border-t border-[rgba(255,255,255,0.05)] px-4 pb-3 pt-3">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgba(201,168,76,0.55)]">
        <Sparkles size={10} />
        Sounds like
      </div>
      <div className="flex flex-wrap gap-1.5">
        {suggestions.map((suggestion) => (
          <SuggestionChip
            key={suggestion.voiceprintId}
            name={displayNameForContactId(suggestion.voiceprintId, contacts)}
            tier={tierForCosine(suggestion.cosine)}
            onClick={() => onPick(suggestion)}
          />
        ))}
      </div>
    </div>
  );
}

interface SuggestionChipProps {
  name: string;
  tier: ConfidenceTier;
  onClick: () => void;
}

function SuggestionChip({ name, tier, onClick }: SuggestionChipProps) {
  // Tier-based styling. Borders + background shift warmth without
  // ever showing a numeric confidence score. Uses the app's amber
  // accent (rgba(201,168,76,…)) as the warm anchor so chips read as
  // part of the Suron dark-amber palette rather than alerting the
  // user to a separate "confidence UI."
  const classes: Record<ConfidenceTier, string> = {
    warmer:
      "border-[rgba(201,168,76,0.55)] bg-[rgba(201,168,76,0.18)] text-[rgba(245,224,170,0.95)]",
    warm:
      "border-[rgba(201,168,76,0.3)] bg-[rgba(201,168,76,0.09)] text-[rgba(232,212,160,0.85)]",
    cool:
      "border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] text-[rgba(255,255,255,0.65)]",
  };
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-[13px] font-medium active:scale-[0.97] active:opacity-80 ${classes[tier]}`}
    >
      {name}
    </button>
  );
}

// ── Slot action row ──

function SlotAction({
  label,
  onClick,
  subtle,
}: {
  label: string;
  onClick: () => void;
  subtle?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`border-t border-[rgba(255,255,255,0.04)] px-4 py-3.5 text-left text-[15px] active:bg-[rgba(255,255,255,0.04)] ${
        subtle ? "text-[rgba(255,255,255,0.45)]" : "text-white"
      }`}
    >
      {label}
    </button>
  );
}

// ── Contact picker — filter synced Apple Contacts ──

function ContactPicker({
  contacts,
  onPick,
  onCancel,
}: {
  contacts: Contact[];
  onPick: (c: Contact) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const pool = contacts.filter((c) => c.appleContactId);
    if (!query) return pool.slice(0, 40);
    return pool
      .filter(
        (c) =>
          c.firstName.toLowerCase().includes(query) ||
          c.lastName.toLowerCase().includes(query) ||
          `${c.firstName} ${c.lastName}`.toLowerCase().includes(query)
      )
      .slice(0, 40);
  }, [contacts, q]);

  return (
    <div className="border-t border-[rgba(255,255,255,0.05)]">
      <div className="flex items-center gap-2 border-b border-[rgba(255,255,255,0.05)] px-4 py-3">
        <Search size={14} className="text-[rgba(255,255,255,0.3)]" />
        <input
          autoFocus
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search your contacts"
          className="flex-1 bg-transparent text-[14px] text-white placeholder:text-[rgba(255,255,255,0.3)] focus:outline-none"
        />
        <button
          onClick={onCancel}
          className="text-[12px] text-[rgba(255,255,255,0.4)] active:text-[rgba(255,255,255,0.7)]"
        >
          Cancel
        </button>
      </div>
      <div className="max-h-[300px] overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="px-4 py-8 text-center text-[13px] text-[rgba(255,255,255,0.3)]">
            {q ? `No match for "${q}"` : "No contacts synced yet."}
          </p>
        ) : (
          filtered.map((c) => (
            <button
              key={c.id}
              onClick={() => onPick(c)}
              className="flex w-full items-center gap-3 border-b border-[rgba(255,255,255,0.04)] px-4 py-3 text-left active:bg-[rgba(255,255,255,0.04)]"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(255,255,255,0.06)]">
                <span className="text-[13px] text-[rgba(255,255,255,0.5)]">
                  {c.firstName[0]}
                  {c.lastName[0]}
                </span>
              </div>
              <span className="flex-1 truncate text-[15px] text-white">
                {c.firstName} {c.lastName}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// ── New-contact form — just a first-name text field for MVP ──

function NewContactForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (firstName: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) onSubmit(name);
      }}
      className="flex flex-col gap-3 border-t border-[rgba(255,255,255,0.05)] px-4 py-4"
    >
      <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgba(255,255,255,0.35)]">
        Name this person
      </label>
      <input
        autoFocus
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="First name"
        className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] px-3 py-2.5 text-[15px] text-white placeholder:text-[rgba(255,255,255,0.3)] focus:border-[rgba(201,168,76,0.4)] focus:outline-none"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-xl bg-[rgba(255,255,255,0.04)] py-2.5 text-[14px] text-[rgba(255,255,255,0.5)] active:bg-[rgba(255,255,255,0.08)]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!name.trim()}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[rgba(201,168,76,0.9)] py-2.5 text-[14px] font-medium text-black disabled:opacity-40"
        >
          <UserPlus size={14} />
          Add
        </button>
      </div>
    </form>
  );
}
